export default {
  howItWorksPage: {
    seoTitle: 'Comment fonctionne Geofleet — De la commande à la preuve de livraison | Geofleet',
    seoDescription:
      'Découvrez comment fonctionne Geofleet en six étapes : importer les commandes, planifier des tournées optimisées, les envoyer à l’application chauffeur, suivre en direct, envoyer un lien de suivi aux clients et recueillir la preuve de livraison — le tout depuis un seul centre de commande avec un Copilot IA.',
    heroBadge: 'Comment ça marche',
    heroTitle: 'Comment fonctionne Geofleet :',
    heroTitleHighlight: 'la commande entre, la preuve de livraison sort',
    /** Direct answer paragraph — written to be quotable by search and AI answer engines. */
    heroLead:
      'Geofleet est un logiciel de gestion des livraisons. Vous importez vos commandes depuis un CSV, votre boutique ou une API. Geofleet planifie des tournées optimisées et les envoie sur les téléphones des chauffeurs. Vous suivez ensuite chaque arrêt en direct, envoyez aux clients un lien de suivi par message et recueillez une preuve de livraison par photo, signature ou OTP. Tout se pilote depuis un seul centre de commande, et un Copilot IA répond aux questions sur ces mêmes données en direct.',
    heroFacts: [
      { icon: 'ri-price-tag-3-line', label: 'Dès 100 $/mois pour 10 chauffeurs' },
      { icon: 'ri-smartphone-line', label: 'L’application chauffeur fonctionne hors ligne' },
      { icon: 'ri-link', label: 'Aucune application à installer pour les clients' },
    ],
    stepsKicker: 'Le cycle de livraison',
    stepsTitle: 'Six étapes de la commande à la preuve de livraison',
    stepsIntro: 'Chaque commande suit les mêmes six étapes dans Geofleet, que vous ayez 5 chauffeurs ou 500.',
    stepLabel: 'Étape {{n}}',
    steps: [
      {
        title: 'Importez vos commandes',
        body: 'Chargez un CSV ou un tableur, synchronisez automatiquement les commandes depuis Shopify et d’autres plateformes OMS, envoyez-les via l’API ou MCP, ou saisissez-les à la main. Les adresses sont géocodées à l’import pour être prêtes à planifier.',
        points: ['Modèles CSV et tableur', 'Shopify, Unicommerce et plus de 10 intégrations OMS', 'REST API et MCP pour vos propres systèmes'],
        linkLabel: 'Voir les intégrations',
        linkHref: '/integrations',
        imageAlt: 'Import d’un CSV de commandes de livraison dans Geofleet',
      },
      {
        title: 'Planifiez des tournées optimisées',
        body: 'Choisissez un profil de planification, par exemple « Livraisons de demain », et générez un plan intelligent. Le moteur de tournées tient compte des créneaux horaires, de la capacité des véhicules, du trafic et de la charge des chauffeurs, puis regroupe les arrêts en trajets.',
        points: ['Créneaux horaires et limites de service', 'Capacité en poids, volume et articles', 'Charge équilibrée entre chauffeurs'],
        linkLabel: 'Planificateur de tournées',
        linkHref: '/features/route-planner',
        imageAlt: 'Smart Planning de Geofleet générant des tournées optimisées à partir d’un profil de planification',
      },
      {
        title: 'Envoyez aux chauffeurs',
        body: 'Vérifiez la distance, la durée et le nombre de commandes de chaque trajet, puis publiez-le. La tournée arrive dans l’application chauffeur avec une navigation pas à pas. Avec Premium, le dispatch et l’affectation automatiques par IA attribuent les trajets à votre place.',
        points: ['Publication en un clic vers l’application chauffeur', 'Fonctionne hors ligne et se synchronise à la reconnexion', 'Dispatch automatique par IA avec Premium'],
        linkLabel: 'Application chauffeur',
        linkHref: '/features/driver-app',
        imageAlt: 'Un trajet publié envoyé à un chauffeur dans Geofleet',
      },
      {
        title: 'Suivez en direct et ajustez',
        body: 'Tous les véhicules et arrêts sont sur une seule carte en direct. Les arrêts en retard ou à risque remontent en tête de file. Vous pouvez réordonner les arrêts, réaffecter des commandes ou ajouter un arrêt en cours de tournée, et l’application du chauffeur se met à jour immédiatement.',
        points: ['Carte en direct avec tournée prévue et réelle', 'Exceptions et risques SLA affichés en premier', 'Réordonnancement par glisser-déposer'],
        linkLabel: 'Rapports et analyses',
        linkHref: '/features/reports-analytics',
        imageAlt: 'Réordonnancement de commandes de livraison sur une carte de tournée en direct dans Geofleet',
      },
      {
        title: 'Tenez les clients informés',
        body: 'Les clients reçoivent un lien de suivi par SMS, e-mail ou WhatsApp. Il s’ouvre dans n’importe quel navigateur et affiche le statut en direct, l’ETA et le chauffeur sur une carte, sans rien à installer. Avec Premium, la page porte votre propre marque.',
        points: ['Mises à jour automatiques aux étapes clés', 'ETA en direct et contact du chauffeur', 'Page de suivi à votre marque avec Premium'],
        linkLabel: 'Suivi à votre marque',
        linkHref: '/features/branded-tracking',
        imageAlt: 'Un client suivant une livraison en direct via un lien de suivi Geofleet sur son téléphone',
      },
      {
        title: 'Prouvez la livraison et mesurez',
        body: 'Les chauffeurs recueillent une photo, une signature électronique, un OTP ou un scan de code-barres à la porte. Chaque preuve est horodatée et géolocalisée. Les résultats alimentent le taux de ponctualité, le coût par livraison et les fiches de performance des chauffeurs, pour savoir quoi corriger demain.',
        points: ['Photo, signature, OTP et code-barres', 'Preuves consultables et exportables', 'KPIs de ponctualité, de coût par livraison et des chauffeurs'],
        linkLabel: 'Preuve de livraison',
        linkHref: '/features/proof-of-delivery',
        imageAlt: 'Chauffeur prenant une photo comme preuve de livraison dans l’application chauffeur Geofleet',
      },
    ],
    surfacesKicker: 'Où vous pilotez',
    surfacesTitle: 'Un seul centre de commande, avec Copilot intégré',
    surfacesIntro:
      'Les six étapes se déroulent au même endroit. Le centre de commande est la vue en direct de votre activité. Copilot est un assistant IA qui lit les mêmes données et peut agir dessus dans la limite de vos permissions.',
    towerSectionTitle: 'Centre de commande',
    towerLead: 'Commandes, chauffeurs, carte en direct, ETA et exceptions sur un seul écran, pour que la prochaine action soit évidente.',
    towerFeatureCards: [
      {
        title: 'Importez vos données à votre façon',
        description: 'Intégrations, MCP, modèles CSV ou saisie manuelle, le tout dans la même liste de commandes.',
      },
      {
        title: 'Planifiez, affectez et adaptez',
        description: 'Réaffectez, réordonnez, ajoutez des arrêts et lancez des mises à jour de masse dans les garde-fous de rôle et de politique.',
      },
      {
        title: 'Une carte, des files priorisées',
        description: 'Les exceptions remontent en tête et chaque rôle voit son périmètre : dispatch, responsables de hub ou partenaires.',
      },
      {
        title: 'Mesurez et auditez',
        description: 'Taux de ponctualité, coût par livraison et fiches de performance, avec un historique complet de qui a modifié quoi et quand.',
      },
    ],
    copilotSectionTitle: 'Copilot',
    copilotLead: 'Posez vos questions sur votre activité en langage naturel. Les réponses proviennent des données en direct, pas d’un texte IA générique.',
    copilotFeatureCards: [
      {
        title: 'Demandez en langage naturel',
        description: '« Quels arrêts de la zone nord sont en retard ? » ou « Qu’est-ce qui a changé depuis 09:00 ? »',
      },
      {
        title: 'Réponses opérationnelles',
        description: 'Listes filtrées, comparaisons et synthèses pour le dispatch, les tournées et les exceptions.',
      },
      {
        title: 'Actions suggérées, avec garde-fous',
        description: 'Copilot propose des reroutages ou des mises à jour de masse que vous confirmez. Il ne peut rien faire que votre rôle n’autorise pas.',
      },
      {
        title: 'Onboarding plus rapide',
        description: 'Des invites guidées pour configurer hubs, intégrations et flux de travail sans jongler avec des tableurs.',
      },
    ],
    faqKicker: 'FAQ',
    faqTitle: 'Questions sur le fonctionnement de Geofleet',
    faqs: [
      {
        question: 'Comment fonctionne Geofleet ?',
        answer:
          'Geofleet reçoit les commandes de livraison depuis un CSV, votre boutique ou une API et planifie des tournées optimisées. Il envoie ces tournées à une application chauffeur, suit chaque arrêt en direct et envoie aux clients un lien de suivi. À la porte, les chauffeurs recueillent une preuve de livraison par photo, signature ou OTP. Les dispatchers pilotent tout depuis un seul centre de commande, avec un Copilot IA pour les questions et les actions de masse.',
      },
      {
        question: 'Comment importer mes commandes dans Geofleet ?',
        answer:
          'Il existe quatre méthodes. Vous pouvez charger un CSV ou un tableur, connecter un OMS ou une boutique comme Shopify ou Unicommerce pour synchroniser les commandes automatiquement, envoyer les commandes via la REST API ou MCP, ou les saisir manuellement. Vous pouvez combiner les méthodes : beaucoup d’équipes commencent par le CSV et ajoutent une intégration ensuite.',
      },
      {
        question: 'Les chauffeurs doivent-ils installer une application ?',
        answer:
          'Oui. Les chauffeurs utilisent l’application chauffeur Geofleet pour recevoir leurs tournées, naviguer, recueillir la preuve de livraison et pointer leur présence. L’application fonctionne hors ligne et se synchronise quand le téléphone se reconnecte.',
      },
      {
        question: 'Les clients doivent-ils installer quelque chose pour suivre une livraison ?',
        answer:
          'Non. Les clients reçoivent un lien de suivi par SMS, e-mail ou WhatsApp qui s’ouvre dans n’importe quel navigateur. Il affiche le statut en direct, l’ETA et la position du chauffeur.',
      },
      {
        question: 'Copilot peut-il modifier des commandes ou des tournées de lui-même ?',
        answer:
          'Uniquement dans la limite des permissions que vous définissez. Copilot respecte le contrôle d’accès par rôle : il ne peut faire que ce que l’utilisateur connecté est autorisé à faire. Il propose des actions, comme des reroutages ou des mises à jour de masse, que vous confirmez.',
      },
      {
        question: 'Combien coûte l’essai de Geofleet ?',
        answer:
          'Les plans commencent avec Geofleet Lite à 100 $ par mois pour 10 chauffeurs et 2 000 commandes, puis 10 $ par chauffeur supplémentaire. Professional coûte $19 et Premium $29 par chauffeur et par mois, facturés trimestriellement. Professional inclut un essai gratuit de 14 jours.',
      },
    ],
    visualHeroAlt: 'Centre de commande Geofleet avec carte en direct, KPIs et liste de commandes',
    visualCommandAlt: 'Centre de commande Geofleet — carte en direct et vue opérationnelle',
    visualCopilotAlt: 'Copilot de Geofleet répondant à une question de dispatch à côté d’une tournée en direct',
    closingTitle: 'Voyez vos propres commandes suivre le parcours',
    closingSub: 'Commencez avec un CSV de commandes réelles, ou réservez une démo et nous passerons en revue votre workflow avec vous.',
  },
};
